import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { MenuItem } from "@mui/material";

import { ActiveIndicator } from "../../../components/ActiveIndicator";
import { HeaderBackButton } from "../../../components/BackButton";
import { CustomButton } from "../../../components/CustomButton";
import { MenuButton } from "../../../components/MenuButton";
import { PaymentStatusButton } from "../../../components/PaymentStatusButton";
import { YearSelect } from "../../../components/SelectComponent";
import { useIsFeatureEnabled } from "../../../hooks/useIsFeatureEnabled";
import { useModal } from "../../../hooks/useModal";
import { COLLECTION_POINTS_PAGE_PATH } from "../../../middleware/routes";
import { updateLocationIsPaidStatusThunk } from "../../../reducers/collectionPointReducer";
import { getSelectedYear } from "../../../selectors/application";
import { getCollectionPointDetail } from "../../../selectors/collectionPoint";
import { InvitationStatus, IUpdateIsPaidStatusRequest, PaymentStatus } from "../../../types/collectionPoint";
import { FeatureName } from "../../../utils/enums";
import { PREV_PAGE_PARAM } from "../../../utils/url";
import { InvitationSentComponent } from "../InvitationSentComponent";
import { InviteToMobileAppModal } from "../InviteToMobileAppModal";

import styles from "./CollectionPointPageHeaderComponent.module.scss";

export const CollectionPointPageHeaderComponent: React.FC = () => {
  const { t } = useTranslation();
  const { name, address, invitationStatus, enableTime, disableTime, billingStatus, locationId, isPaid } =
    useSelector(getCollectionPointDetail);
  const selectedYear = useSelector(getSelectedYear);
  const inviteToMobileAppModal = useModal();
  const mobileEnabled = useIsFeatureEnabled(FeatureName.mobile);

  const exportToPdf = () => {
    print();
  };

  const getBackTo = () => {
    const queryParams = new URLSearchParams(location.search);
    const prevPageValue = queryParams.get(PREV_PAGE_PARAM);

    return prevPageValue || COLLECTION_POINTS_PAGE_PATH;
  };

  const payload: IUpdateIsPaidStatusRequest = {
    locationId,
    isPaid: isPaid ? PaymentStatus.Paid : PaymentStatus.Unpaid,
    year: selectedYear.value,
  };

  return (
    <>
      <div className={styles.container}>
        <HeaderBackButton
          backTo={getBackTo()}
          title={
            <div className={styles.nameWrapper}>
              <p>{name}</p>
              <ActiveIndicator enableTime={enableTime} disableTime={disableTime} />
            </div>
          }
          subtitle={address}
        />
        {!billingStatus && (
          <div className={styles.center}>
            <p>{t("isPaid")}:</p>
            <div className={styles.buttonsContainer}>
              <PaymentStatusButton
                isPaid={isPaid}
                translationKey={"collectionPoint.paymentModal"}
                payload={payload}
                thunk={updateLocationIsPaidStatusThunk}
              />
            </div>
          </div>
        )}
        <div className={styles.controls}>
          <YearSelect />
          {mobileEnabled && (
            <>
              {invitationStatus === InvitationStatus.NotInvited ? (
                <CustomButton
                  className={styles.inviteToMobileAppBtn}
                  text={t("inviteToMobileApp")}
                  onClick={inviteToMobileAppModal.toggleModalVisibility}
                />
              ) : (
                <InvitationSentComponent />
              )}
            </>
          )}
          <MenuButton>
            <MenuItem onClick={exportToPdf}>{t("ExportToPdf")}</MenuItem>
          </MenuButton>
        </div>
      </div>
      <InviteToMobileAppModal
        isModalVisible={inviteToMobileAppModal.isModalVisible}
        toggleModalVisibility={inviteToMobileAppModal.toggleModalVisibility}
      />
    </>
  );
};
